import { AIStarsCutout, AIStarsIcon, ChatGroupIcon } from '../../../assets/svg';
import { lightTheme } from '../../../styles/theme';
import { ConversationType } from '../../../types/conversation.types';
import { Circle } from './ConversationAvatar.styled';

/**
 * Round avatar for a conversation or a person.
 *
 * Deliberately absent from the thread itself - the bubbles are aligned and
 * labelled, and avatars beside every message were tried and removed. It earns
 * its place in lists, where rows are otherwise indistinguishable at a glance
 * and a group needs to be tellable from a person before you read the name.
 *
 * Groups show their uploaded image when they have one and a pair of figures when
 * they do not; a person falls back to initials on the brand red. An earlier
 * version picked from a palette keyed on the id, which gave the list a row of
 * unrelated colours that read as meaning something - a status, a type - when it
 * meant nothing at all.
 *
 * Initials are a person's, not a group's: "DT" over a group called Dev Team
 * reads as somebody named that, and in a list where every other disc IS a
 * person, the one thing worth saying at a glance is which rows are not.
 *
 * Viktor (shown as "vGrubs AI") is the third kind: the AI stars on the AI
 * gradient, white - and, where the surface is already the gradient (the open
 * entry card), a white disc with the stars cut out of it so the card's moving
 * gradient IS the mark.
 * The same flip a selected row gives its avatar, done with a hole rather than
 * a second gradient. The backend sends no image for him on purpose - the
 * avatar is the frontend's.
 */

export const conversationInitials = (name?: string | null) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase() || '?';

export const ConversationAvatar = ({
  name,
  imageUrl,
  type,
  size = 36,
  inverted,
}: {
  name?: string | null;
  imageUrl?: string | null;
  /** Only groups (both kinds) carry an image; Viktor wears the AI stars; the
      rest always render initials. */
  type?: ConversationType;
  size?: number;
  /** Set when the avatar sits on the brand red rather than on a light surface. */
  inverted?: boolean;
}) => (
  <Circle $size={size} $inverted={inverted} $viktor={type === 'viktor'}>
    {type === 'viktor' ? (
      inverted ? (
        <AIStarsCutout />
      ) : (
        <AIStarsIcon color={lightTheme.white} />
      )
    ) : type === 'group' || type === 'merchantGroup' ? (
      imageUrl ? (
        <img src={imageUrl} alt="" />
      ) : (
        /* The disc turns white on a selected row, so the glyph has to turn red
           with it - see `inverted` above. */
        <ChatGroupIcon
          color={inverted ? lightTheme.primary : lightTheme.white}
        />
      )
    ) : (
      conversationInitials(name)
    )}
  </Circle>
);
